<template>
  <span
      :class="[
      props.pill ? 'rounded-[14px]' : 'rounded-md',
      props.flat ? '' : 'ring-1 ring-inset ring-gray-500/10',
      `inline-flex w-fit h-fit items-center ${variantClasses[props.variant]?.background || variantClasses.default.background} ${variantClasses[props.variant]?.title || variantClasses.default.title} capitalize px-2 py-1 text-xs gap-1`,
      {},
    ]"
  >
    <slot name="prepend-icon">
      <svg
          v-if="props.dotted"
          viewBox="0 0 6 6"
          aria-hidden="true"
          :class="`size-1.5 ${variantClasses[props.variant].svg}`"
      >
        <circle cx="3" cy="3" r="3" />
      </svg>
    </slot>

    <slot />

    <button
        v-if="props.remove"
        type="button"
        :class="[
        `group relative -mr-1 size-3.5 rounded-sm hover:${variantClasses[props.variant].background}/20`,
      ]"
        @click.stop="emit('remove', $event)"
    >
      <span class="sr-only">Remove</span>
      <svg
          viewBox="0 0 14 14"
          :class="[
          `size-3.5 ${variantClasses[props.variant].svg} group-hover:${variantClasses[props.variant].svg}/75`,
        ]"
      >
        <path d="M4 4l6 6m0-6l-6 6" />
      </svg>
      <span class="absolute -inset-1" />
    </button>
  </span>
</template>

<script setup lang="ts">
interface Props {
  dotted?: boolean;
  pill?: boolean;
  remove?: boolean;
  flat?: boolean;
  small?: boolean;
  variant?:
      | "primary"
      | "filled"
      | "success"
      | "danger"
      | "warning"
      | "information"
      | "dark_blue"
      | "ongoing"
      | "skill"
      | "default"
      | "destructive"
      | "invitation_waiting"
      | "invitation_interested"
      | "invitation_declined";
}

const props = withDefaults(defineProps<Props>(), {
  variant: "default",
});
const emit = defineEmits(["remove"]);

const variantClasses = {
  default: {
    svg: "fill-gray-400",
    background: "bg-gray-700 border border-gray-600",
    title: "text-gray-100",
  },
  primary: {
    svg: "fill-primary",
    background: "bg-primary bg-opacity-20 border border-primary",
    title: "text-gray-100",
  },
  filled: {
    svg: "fill-white",
    background: "bg-primary",
    title: "text-white",
  },
  success: {
    svg: "fill-success",
    background: "bg-success bg-opacity-20 border border-success",
    title: "text-white",
  },
  danger: {
    svg: "fill-error",
    background: "bg-error bg-opacity-20",
    title: "text-error",
  },
  warning: {
    svg: "fill-orange-400",
    background: "bg-orange-700 bg-opacity-20",
    title: "text-orange-100",
  },
  information: {
    svg: "fill-blue-500",
    background: "bg-blue-800 bg-opacity-20 border border-blue-500",
    title: "text-white",
  },
  "dark_blue": {
    svg: "fill-blue-500",
    background: "bg-blue-500 bg-opacity-20 border border-blue-400",
    title: "text-white",
  },
  "ongoing": {
    svg: "fill-[#526EFF]",
    background: "bg-[#303550] border border-[#393F79]",
    title: "text-[#526EFF]",
  },
  "skill": {
    svg: "fill-white",
    background: "bg-[#3B3B3B] border border-[#515154]",
    title: "text-white font-light text-xs",
  },
  "destructive": {
    svg: "fill-error",
    background: "bg-[#503033] border border-[#ae171a]",
    title: "text-white",
  },
  "invitation_waiting": {
    svg: "fill-gray-400",
    background: "bg-[#3B3B3B] border border-[#515154]",
    title: "text-gray-200",
  },
  "invitation_interested": {
    svg: "fill-green-400",
    background: "bg-[#44513c] border border-[#649343]",
    title: "text-white",
  },
  "invitation_declined": {
    svg: "fill-red-400",
    background: "bg-[#503033] border border-[#ae171a]",
    title: "text-white",
  }
};
</script>

